import type React from 'react';
import { useCallback, useState } from 'react';
import { CustomerApiError, customerApi } from '../customer_api';

interface Props {
  bookingId: string;
  /** Determines the default arrival kind shown in the drawer. */
  defaultKind?: 'dropoff' | 'pickup';
  onSuccess: () => void;
  onClose: () => void;
}

type EtaPreset = '15min' | '30min' | '1hr' | 'custom';

const PRESET_MINUTES: Record<Exclude<EtaPreset, 'custom'>, number> = {
  '15min': 15,
  '30min': 30,
  '1hr': 60,
};

/**
 * OnMyWayDrawer — customer "I'm on my way" CTA on /account/bookings/:id.
 *
 * Posts to POST /api/customer/bookings/:id/arrivals with an ETA + optional
 * vehicle plate + optional note. Migration 0079 — drop-off + pick-up
 * choreography.
 */
export function OnMyWayDrawer({ bookingId, defaultKind = 'pickup', onSuccess, onClose }: Props) {
  const [kind, setKind] = useState<'dropoff' | 'pickup'>(defaultKind);
  const [preset, setPreset] = useState<EtaPreset>('15min');
  const [customMinutes, setCustomMinutes] = useState<number>(20);
  const [vehiclePlate, setVehiclePlate] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const computeEtaIso = useCallback((): string => {
    const minutes = preset === 'custom' ? customMinutes : PRESET_MINUTES[preset];
    return new Date(Date.now() + Math.max(1, minutes) * 60_000).toISOString();
  }, [preset, customMinutes]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (submitting) return;
      setSubmitting(true);
      setError(null);
      try {
        await customerApi(`/api/customer/bookings/${bookingId}/arrivals`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            kind,
            etaAt: computeEtaIso(),
            vehiclePlate: vehiclePlate.trim() || null,
            notes: notes.trim() || null,
          }),
        });
        onSuccess();
      } catch (err) {
        const msg =
          err instanceof CustomerApiError
            ? `Could not send (status ${err.status}). Please try again.`
            : 'Could not send. Please try again.';
        setError(msg);
        setSubmitting(false);
      }
    },
    [bookingId, kind, computeEtaIso, vehiclePlate, notes, submitting, onSuccess],
  );

  return (
    <div
      role="dialog"
      aria-label="On my way"
      data-testid="on-my-way-drawer"
      style={{
        marginTop: '1rem',
        padding: '1.25rem',
        borderRadius: '12px',
        border: '1px solid var(--border, oklch(0.915 0.005 80))',
        background: 'var(--surface, white)',
      }}
    >
      <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem', fontWeight: 600 }}>I'm on my way</h3>
      <form onSubmit={handleSubmit}>
        <label style={{ display: 'block', marginBottom: '0.75rem', fontSize: '0.85rem' }}>
          Visit type
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value as 'dropoff' | 'pickup')}
            data-testid="kind-select"
            style={{
              display: 'block',
              width: '100%',
              marginTop: '0.25rem',
              padding: '0.5rem',
              borderRadius: '8px',
              border: '1px solid var(--border, oklch(0.915 0.005 80))',
            }}
          >
            <option value="pickup">Pick up</option>
            <option value="dropoff">Drop off</option>
          </select>
        </label>
        <fieldset style={{ border: 'none', padding: 0, margin: '0 0 0.75rem' }}>
          <legend style={{ fontSize: '0.85rem', marginBottom: '0.4rem' }}>ETA</legend>
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
            {(['15min', '30min', '1hr', 'custom'] as EtaPreset[]).map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => setPreset(opt)}
                data-testid={`eta-${opt}`}
                style={{
                  padding: '0.4rem 0.75rem',
                  borderRadius: '999px',
                  border:
                    preset === opt
                      ? '1px solid var(--primary, oklch(0.43 0.18 263))'
                      : '1px solid var(--border, oklch(0.915 0.005 80))',
                  background:
                    preset === opt ? 'var(--primary, oklch(0.43 0.18 263))' : 'transparent',
                  color: preset === opt ? 'white' : 'inherit',
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                }}
              >
                {opt === '15min'
                  ? '15 min'
                  : opt === '30min'
                    ? '30 min'
                    : opt === '1hr'
                      ? '1 hour'
                      : 'Custom'}
              </button>
            ))}
          </div>
          {preset === 'custom' && (
            <div style={{ marginTop: '0.5rem' }}>
              <label style={{ fontSize: '0.85rem' }}>
                Minutes from now
                <input
                  type="number"
                  min={1}
                  max={300}
                  value={customMinutes}
                  onChange={(e) => setCustomMinutes(Math.max(1, Number(e.target.value)))}
                  data-testid="eta-custom-minutes"
                  style={{
                    display: 'block',
                    width: '100%',
                    marginTop: '0.25rem',
                    padding: '0.5rem',
                    borderRadius: '8px',
                    border: '1px solid var(--border, oklch(0.915 0.005 80))',
                  }}
                />
              </label>
            </div>
          )}
        </fieldset>
        <label style={{ display: 'block', marginBottom: '0.75rem', fontSize: '0.85rem' }}>
          Vehicle plate (optional)
          <input
            type="text"
            value={vehiclePlate}
            onChange={(e) => setVehiclePlate(e.target.value)}
            maxLength={20}
            data-testid="vehicle-plate"
            placeholder="ABC 123"
            style={{
              display: 'block',
              width: '100%',
              marginTop: '0.25rem',
              padding: '0.5rem',
              borderRadius: '8px',
              border: '1px solid var(--border, oklch(0.915 0.005 80))',
              textTransform: 'uppercase',
            }}
          />
        </label>
        <label style={{ display: 'block', marginBottom: '0.75rem', fontSize: '0.85rem' }}>
          Note (optional)
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={500}
            data-testid="arrival-notes"
            rows={2}
            style={{
              display: 'block',
              width: '100%',
              marginTop: '0.25rem',
              padding: '0.5rem',
              borderRadius: '8px',
              border: '1px solid var(--border, oklch(0.915 0.005 80))',
              fontFamily: 'inherit',
            }}
          />
        </label>
        {error && (
          <div
            role="alert"
            data-testid="arrival-error"
            style={{ marginBottom: '0.75rem', color: 'oklch(0.55 0.18 25)', fontSize: '0.85rem' }}
          >
            {error}
          </div>
        )}
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            type="submit"
            disabled={submitting}
            data-testid="arrival-submit"
            style={{
              flex: 1,
              padding: '0.75rem',
              borderRadius: '10px',
              background: 'var(--primary, oklch(0.43 0.18 263))',
              color: 'white',
              border: 'none',
              fontWeight: 500,
              cursor: submitting ? 'wait' : 'pointer',
              opacity: submitting ? 0.7 : 1,
            }}
          >
            {submitting ? 'Sending…' : 'Let them know'}
          </button>
          <button
            type="button"
            onClick={onClose}
            data-testid="arrival-cancel"
            style={{
              padding: '0.75rem 1rem',
              borderRadius: '10px',
              background: 'transparent',
              border: '1px solid var(--border, oklch(0.915 0.005 80))',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
